import React from 'react';
import {
  Box,
  Chip,
  Divider,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Typography,
  alpha,
} from '@mui/material';
import {
  HelpOutline as HelpOutlineIcon,
  Logout as LogoutIcon,
  OpenInNew as OpenInNewIcon,
  ReceiptLong as ReceiptLongIcon,
  SettingsOutlined as SettingsOutlinedIcon,
  CloudDownload as CloudDownloadIcon,
  DashboardOutlined as DashboardOutlinedIcon,
} from '@mui/icons-material';
import { getConfig } from '../../config/appConfig';
import { defaultFrontendOrigin, defaultSupportEmail } from '../../config/environments';
import toast from 'react-hot-toast';
import { resolveDownloadUrl, triggerBrowserDownload, trackDirectDownload } from '../../utils/downloadResume';
import UserAvatar from './UserAvatar';

const SUPPORT_EMAIL = getConfig('app.supportEmail') || defaultSupportEmail();
const SUPPORT_SITE_URL = getConfig('app.supportSiteUrl') || defaultFrontendOrigin();

function buildSupportMailtoHref(user, planLabel) {
  const params = new URLSearchParams();
  params.set('subject', 'Candor Subscription — Help Request');
  const lines = [
    'Hello Candor Support,',
    '',
    `Account email: ${user?.email || '(unknown)'}`,
    planLabel ? `Plan: ${planLabel}` : null,
    '',
    'Please describe your issue:',
    '',
  ].filter(Boolean);
  params.set('body', lines.join('\n'));
  return `mailto:${SUPPORT_EMAIL}?${params.toString()}`;
}

const menuItemSx = {
  display: 'flex',
  alignItems: 'center',
  py: 1.1,
  px: 1.5,
  mx: 1,
  my: 0.25,
  borderRadius: 1.5,
  gap: 1.25,
  '&:hover': {
    backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08),
  },
};

function MenuIconBadge({ children, color = 'primary' }) {
  return (
    <Box
      sx={{
        width: 36,
        height: 36,
        borderRadius: 1.5,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        bgcolor: (theme) => alpha(theme.palette[color].main, 0.1),
        color: `${color}.main`,
      }}
    >
      {children}
    </Box>
  );
}

function getPlanLabel(subscription) {
  const raw =
    subscription?.plan_name ||
    subscription?.plan?.name ||
    subscription?.plan_id ||
    '';
  if (!raw) return null;
  return String(raw).replace(/_/g, ' ');
}

const UserAccountMenu = ({
  anchorEl,
  open,
  onClose,
  user,
  subscription,
  onNavigate,
  onSignOut,
}) => {
  const planLabel = getPlanLabel(subscription);
  const displayName = user?.name || 'Account';
  const displayEmail = user?.email || '';

  const handleNav = (sectionId) => {
    onClose();
    if (onNavigate) onNavigate(sectionId);
  };

  const supportMailtoHref = buildSupportMailtoHref(user, planLabel);

  const handleHelpMailto = () => {
    onClose();
    toast.success('Opening your email app…', { duration: 2500 });
  };

  const handleHelpWebsite = () => {
    onClose();
    window.open(SUPPORT_SITE_URL, '_blank', 'noopener,noreferrer');
  };

  const handleSignOut = () => {
    onClose();
    if (onSignOut) onSignOut();
  };

  const handleDownloadIDE = async () => {
    onClose();
    try {
      const downloadLink = await resolveDownloadUrl();
      triggerBrowserDownload(downloadLink);
      toast.success('Your Candor IDE download has started!');
      trackDirectDownload(user, downloadLink, 'user_account_menu');
    } catch (err) {
      console.error('Download failed:', err);
      toast.error('Failed to trigger download.');
    }
  };

  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      elevation={0}
      slotProps={{
        paper: {
          sx: {
            mt: 1.25,
            minWidth: 300,
            maxWidth: 320,
            borderRadius: 2.5,
            overflow: 'visible',
            boxShadow: (theme) =>
              theme.palette.mode === 'dark'
                ? '0 12px 40px rgba(0,0,0,0.45)'
                : '0 12px 40px rgba(15, 23, 42, 0.14)',
            border: 1,
            borderColor: 'divider',
            '&::before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 20,
              width: 12,
              height: 12,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
              borderLeft: 1,
              borderTop: 1,
              borderColor: 'divider',
            },
          },
        },
      }}
      transformOrigin={{ horizontal: 'right', vertical: 'top' }}
      anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
    >
      <Box
        sx={{
          px: 2,
          pt: 2,
          pb: 1.75,
          position: 'relative',
          zIndex: 1,
          background: (theme) =>
            theme.palette.mode === 'dark'
              ? `linear-gradient(145deg, ${alpha(theme.palette.primary.dark, 0.35)} 0%, ${alpha(theme.palette.background.paper, 1)} 70%)`
              : `linear-gradient(145deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${theme.palette.background.paper} 65%)`,
        }}
      >
        <Stack direction="row" spacing={1.75} alignItems="center">
          <UserAvatar
            user={user}
            name={displayName}
            sx={{
              width: 48,
              height: 48,
              background: 'linear-gradient(145deg, #2563eb 0%, #0d9488 100%)',
              fontSize: '0.95rem',
              fontWeight: 700,
              boxShadow: '0 4px 14px rgba(37, 99, 235, 0.35)',
            }}
          />
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="subtitle2" fontWeight={700} noWrap>
              {displayName}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', lineHeight: 1.4 }}
              noWrap
              title={displayEmail}
            >
              {displayEmail}
            </Typography>
            {planLabel && (
              <Chip
                label={planLabel}
                size="small"
                sx={{
                  mt: 0.75,
                  height: 22,
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  textTransform: 'capitalize',
                }}
              />
            )}
          </Box>
        </Stack>
      </Box>

      <Divider sx={{ my: 0.5 }} />

      <Typography
        variant="overline"
        sx={{ px: 2.5, pt: 0.75, pb: 0.25, display: 'block', color: 'text.secondary', fontSize: '0.65rem' }}
      >
        Account
      </Typography>

      <MenuItem onClick={handleDownloadIDE} sx={menuItemSx}>
        <MenuIconBadge color="success">
          <CloudDownloadIcon fontSize="small" />
        </MenuIconBadge>
        <ListItemText
          primary="Download IDE"
          secondary="Get the desktop application"
          primaryTypographyProps={{ fontWeight: 600, fontSize: '0.875rem' }}
          secondaryTypographyProps={{ fontSize: '0.75rem' }}
        />
      </MenuItem>

      <MenuItem onClick={() => handleNav('dashboard')} sx={menuItemSx}>
        <MenuIconBadge color="primary">
          <DashboardOutlinedIcon fontSize="small" />
        </MenuIconBadge>
        <ListItemText
          primary="Dashboard"
          secondary="Subscription overview"
          primaryTypographyProps={{ fontWeight: 600, fontSize: '0.875rem' }}
          secondaryTypographyProps={{ fontSize: '0.75rem' }}
        />
      </MenuItem>

      <MenuItem onClick={() => handleNav('settings')} sx={menuItemSx}>
        <MenuIconBadge color="primary">
          <SettingsOutlinedIcon fontSize="small" />
        </MenuIconBadge>
        <ListItemText
          primary="Profile & settings"
          secondary="Account preferences"
          primaryTypographyProps={{ fontWeight: 600, fontSize: '0.875rem' }}
          secondaryTypographyProps={{ fontSize: '0.75rem' }}
        />
      </MenuItem>

      <MenuItem onClick={() => handleNav('billing')} sx={menuItemSx}>
        <MenuIconBadge color="info">
          <ReceiptLongIcon fontSize="small" />
        </MenuIconBadge>
        <ListItemText
          primary="Billing history"
          secondary="Invoices & receipts"
          primaryTypographyProps={{ fontWeight: 600, fontSize: '0.875rem' }}
          secondaryTypographyProps={{ fontSize: '0.75rem' }}
        />
      </MenuItem>

      <Divider sx={{ my: 1 }} />

      <Typography
        variant="overline"
        sx={{ px: 2.5, pb: 0.25, display: 'block', color: 'text.secondary', fontSize: '0.65rem' }}
      >
        Support
      </Typography>

      <MenuItem
        component="a"
        href={supportMailtoHref}
        onClick={handleHelpMailto}
        sx={menuItemSx}
        rel="noopener noreferrer"
      >
        <MenuIconBadge color="secondary">
          <HelpOutlineIcon fontSize="small" />
        </MenuIconBadge>
        <ListItemText
          primary="Email support"
          secondary={SUPPORT_EMAIL}
          primaryTypographyProps={{ fontWeight: 600, fontSize: '0.875rem' }}
          secondaryTypographyProps={{ fontSize: '0.75rem' }}
        />
      </MenuItem>

      <MenuItem onClick={handleHelpWebsite} sx={{ ...menuItemSx, mb: 0.5 }}>
        <MenuIconBadge color="secondary">
          <OpenInNewIcon fontSize="small" />
        </MenuIconBadge>
        <ListItemText
          primary="Help center"
          secondary="candorsys.com"
          primaryTypographyProps={{ fontWeight: 600, fontSize: '0.875rem' }}
          secondaryTypographyProps={{ fontSize: '0.75rem' }}
        />
      </MenuItem>

      <Divider sx={{ my: 0.75 }} />

      <MenuItem
        onClick={handleSignOut}
        sx={{
          ...menuItemSx,
          mb: 0.75,
          color: 'error.main',
          '&:hover': {
            backgroundColor: (theme) => alpha(theme.palette.error.main, 0.08),
          },
        }}
      >
        <MenuIconBadge color="error">
          <LogoutIcon fontSize="small" />
        </MenuIconBadge>
        <ListItemText
          primary="Sign out"
          secondary="End session securely"
          primaryTypographyProps={{ fontWeight: 600, fontSize: '0.875rem' }}
          secondaryTypographyProps={{ fontSize: '0.75rem', sx: { color: 'text.secondary' } }}
        />
      </MenuItem>
    </Menu>
  );
};

export default UserAccountMenu;
